import React from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import defaultpic from "../../assets/DefaultPic/profile.png";

const bedstip = () => <Tooltip>Beds</Tooltip>;
const bathstip = () => <Tooltip>Bathrooms</Tooltip>;
const areatip = () => <Tooltip>Square Feet</Tooltip>;

/**
 * Reusable listing card with Best Deal of the Month design.
 * Use across frontend for consistent card UI.
 * @param {Object} listing - API listing object { _id, BasicInformation, Details, Gallery, Author, Admin, Location, createdAt }
 * @param {boolean} showAuthor - Show author row (default true)
 * @param {boolean} showLocation - Show location text (default true)
 * @param {boolean} showViewDetails - Show View Details button (default true)
 * @param {string} className - Extra wrapper class
 * @param {Function} t - Translation function (optional)
 */
const ListingCard = ({
  listing,
  showAuthor = true,
  showLocation = true,
  showViewDetails = true,
  className = "",
  t = (key) => key,
}) => {
  if (!listing) return null;

  const basicInformation = listing?.BasicInformation || {};
  const details = listing?.Details || {};
  const gallery = listing?.Gallery || {};
  const author = listing?.Author || listing?.Admin;
  const location = listing?.Location || {};
  const country = location.country === "undefined" || !location.country ? "Thailand" : location.country;
  const province = location.provice === "undefined" || !location.provice ? "Bangkok" : location.provice;
  const city = location.city === "undefined" || !location.city ? "Bangkok" : location.city;
  const imageUrl = Array.isArray(gallery?.file) ? gallery?.file?.[0] : (gallery?.file || defaultpic);
  const listingType = basicInformation?.listingType;
  const isRent = listingType === "Rent";

  return (
    <div className={`best-deal-card-wrap ${className}`.trim()}>
      <div className="listing listing-list myBestdealofMonth best-deal-card">
        <div className="listing-thumbnail best-deal-card__image">
          <Link to={`/listing-details/${listing._id}`}>
            <img
              src={imageUrl}
              alt={basicInformation?.name || "listing"}
              className="best-deal-card__img"
            />
          </Link>
          <div className="listing-badges classBadge">
            {basicInformation?.status?.split(", ").includes("Rental") && (
              <span className="listing-badge rent">{t("Rental")}</span>
            )}
            {basicInformation?.status?.split(", ").includes("For Sale") && (
              <span className="listing-badge sale">{t("On Sale")}</span>
            )}
          </div>
        </div>
        <div className="listing-body best-deal-card__body">
          {showAuthor && (
            <div className="best-deal-card__author">
              <div className="best-deal-card__author-info">
                <img src={author?.pic || defaultpic} alt="author" />
                <div className="best-deal-card__author-text">
                  <p className="best-deal-card__author-name">
                    <Link to="#">{author?.name || "-"}</Link>
                  </p>
                  <span className="best-deal-card__date">
                    {listing?.createdAt?.split("T")[0] || ""}
                  </span>
                </div>
              </div>
              <span className="best-deal-card__country">{country}</span>
            </div>
          )}
          <h5 className="listing-title best-deal-card__title">
            <Link
              to={`/listing-details/${listing._id}`}
              title={basicInformation?.name}
            >
              {basicInformation?.name || "-"}
            </Link>
          </h5>
          <div className="best-deal-card__prices">
            {basicInformation?.originalprice && (
              <div className="best-deal-card__price-row">
                <span className="best-deal-card__price-label">Original:</span>
                <span className="best-deal-card__price-value">
                  {basicInformation.currency}
                  {Number(basicInformation.originalprice).toLocaleString()}
                </span>
              </div>
            )}
            <div className="best-deal-card__price-row">
              <span className="best-deal-card__price-label">
                {isRent ? "Rental:" : "Selling:"}
              </span>
              <span className="best-deal-card__price-value best-deal-card__price-value--main">
                {basicInformation?.currency}
                {isRent
                  ? (basicInformation.rentalPrice
                      ? Number(basicInformation.rentalPrice).toLocaleString()
                      : "")
                  : (basicInformation.price?.toString().length > 10
                      ? basicInformation.price.toString().slice(0, 10) + "..."
                      : Number(basicInformation.price || 0).toLocaleString())}
              </span>
            </div>
          </div>
          <p className="listing-text best-deal-card__description">
            {basicInformation?.description?.length > 70
              ? basicInformation.description.slice(0, 70) + "..."
              : basicInformation?.description || ""}
          </p>
          <div className="acr-listing-icons best-deal-card__icons">
            <OverlayTrigger overlay={bedstip}>
              <div className="acr-listing-icon">
                <i className="flaticon-bedroom" />
                <span className="acr-listing-icon-value">
                  {details?.beds ?? "-"}
                </span>
              </div>
            </OverlayTrigger>
            <OverlayTrigger overlay={bathstip}>
              <div className="acr-listing-icon">
                <i className="flaticon-bathroom" />
                <span className="acr-listing-icon-value">
                  {details?.bathrooms ?? "-"}
                </span>
              </div>
            </OverlayTrigger>
            <OverlayTrigger overlay={areatip}>
              <div className="acr-listing-icon">
                <i className="flaticon-ruler" />
                <span className="acr-listing-icon-value">
                  {basicInformation?.space ?? "-"} SQM
                </span>
              </div>
            </OverlayTrigger>
          </div>
          {showLocation && (
            <div className="best-deal-card__location">
              <span className="best-deal-card__location-text">
                {province}
                {city ? ` / ${city}` : ""}
              </span>
            </div>
          )}
          {showViewDetails && (
            <div className="best-deal-card__footer">
              <Link
                to={`/listing-details/${listing._id}`}
                className="btn-custom btn-sm secondary best-deal-card__btn"
              >
                {t("viewDetails")}
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
